import React from 'react';
import { Button, Flex, Text } from '@chatsdet/ui';
import {
  MagnifyingGlass,
  Plus,
  Code,
  Layout,
  Cube,
  Sliders,
} from '@phosphor-icons/react';

type Props = {
  onBootstrap: () => void;
  onAddManually: () => void;
  hasLegacyContext: boolean;
  onImportLegacy?: () => void;
  isBootstrapping: boolean;
};

const SECTIONS = [
  {
    icon: Layout,
    label: 'General',
    description: 'App overview, navigation, core concepts',
  },
  {
    icon: Code,
    label: 'Technology',
    description: 'Framework, API type, auth patterns',
  },
  {
    icon: Cube,
    label: 'Product',
    description: 'Domain terms, entity types, business rules',
  },
  {
    icon: Sliders,
    label: 'UI Patterns',
    description: 'Selects, date pickers, modals, forms',
  },
];

export function EmptyContextState({
  onBootstrap,
  onAddManually,
  hasLegacyContext,
  onImportLegacy,
  isBootstrapping,
}: Props) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-8">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
            <MagnifyingGlass size={24} className="text-amber-500" weight="duotone" />
          </div>
          <Text size="lg" weight="semibold" className="text-foreground mb-1">
            Teach agents about your app
          </Text>
          <Text size="sm" className="text-muted-foreground max-w-sm mx-auto">
            Application context helps agents navigate your app correctly. Bootstrap it by
            letting an agent explore, or add details manually.
          </Text>
        </div>

        {/* Section preview cards */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {SECTIONS.map(({ icon: Icon, label, description }) => (
            <div
              key={label}
              className="rounded-lg border border-dashed border-border bg-card/50 px-4 py-3"
            >
              <Flex align="center" gap="sm" className="mb-1">
                <Icon size={16} className="text-muted-foreground" weight="duotone" />
                <Text size="sm" weight="medium" className="text-foreground">
                  {label}
                </Text>
              </Flex>
              <Text size="xs" className="text-muted-foreground">
                {description}
              </Text>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="text-center">
          <Flex gap="md" align="center" justify="center">
            <Button variant="primary" onPress={onBootstrap} isDisabled={isBootstrapping}>
              <MagnifyingGlass size={16} />
              {isBootstrapping ? 'Bootstrapping...' : 'Bootstrap Context'}
            </Button>
            <Button variant="secondary" onPress={onAddManually}>
              <Plus size={16} />
              Add Manually
            </Button>
          </Flex>

          {hasLegacyContext && onImportLegacy && (
            <button
              onClick={onImportLegacy}
              className="mt-4 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            >
              Import from existing agent context
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
